import React, { useState, useEffect, useRef } from 'react';

import { GoogleMap } from '@react-google-maps/api';
import { calculateDistance } from '../Helpers'
import { addMarkersAndClusterer } from '../../../_Shared/Scripts/MapsMarkersAndClusterer';

import MapControlsWidget from './MapControls';
import LoadingScreenWidget from './LoadingScreen';
import InfoWindowWidget from './InfoWindow';
import MapMarkerIcon from '../../../../../publicResources/icons/map-marker-unselected.svg';
import MapMarkerIconSelected from '../../../../../publicResources/icons/map-marker-selected.svg';
import MapMarkerRoofIcon from '../../../../../publicResources/icons/map-marker-roof-unselected.svg';
import MapMarkerRoofIconSelected from '../../../../../publicResources/icons/map-marker-roof-selected.svg';
import MapMarkerIconCenter from '../../../../../publicResources/icons/map-marker-red.svg';

let mapOptions = {
  mapId: 'c450492765080973',
  minZoom: 8,
  maxZoom: 18,
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: 'cooperative',
};

const MapWidget = ({ isLoaded, cityPageContext, setCityPageContext, viewToggle, setViewToggle }) => {

  const lang = cityPageContext.localization.inputs;
  const refContainer = useRef(null);
  const [requestAnimation, setRequestAnimation] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);

  const [mapRef, setMapRef] = useState(null);
  const [zoom, setZoom] = useState(15);
  const [lastKnownZoom, setLastKnownZoom] = useState(15);
  const [center, setCenter] = useState(cityPageContext.selectedLocation.position ?? { lat: 59.329, lng: 18.068 });
  const [lastKnownLocation, setLastKnownLocation] = useState(cityPageContext.selectedLocation.position ?? { lat: 59.329, lng: 18.068 });

  const [reloadButtonVisible, setReloadButtonVisible] = useState(false);
  const [reloadButtonPending, setReloadButtonPending] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [markerMap, setMarkerMap] = useState({});
  const [markerClusterer, setMarkerClusterer] = useState(null);

  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [keyboardEvent, setKeyboardEvent] = useState(false);
  const [disableReload, setDisableReload] = useState(false);

  const [freeTextFilter, setFreeTextFilter] = useState(null);

  const contextMapPosition = cityPageContext.selectedLocation.position;
  const returnToOriginalPosition = cityPageContext.returnToOriginalPosition;
  const contextMapZoom = cityPageContext.mapZoom;
  const contextLastKnownLocation = cityPageContext.lastKnownLocation;
  const contextFilteredFacilities = cityPageContext.filteredFacilities;

  const getZIndex = (lat) => Math.round((lat - Math.floor(lat)) * -10000);

  const selectedLang = document.documentElement.getAttribute("lang");
  const markerIconAlternativeText = selectedLang == "sv" ? "markörikon" : selectedLang == "fi" ? "merkkikuvake" : selectedLang == "no" ? "markørikon" : "marker icon";

  let showOnMapButtons;
  let freeTextInputField;

  useEffect(() => {
    if (cityPageContext.selectedLocation.position)
      setCenter(cityPageContext.selectedLocation.position);
    if (zoom && mapRef) mapRef.setZoom(zoom);
  }, [contextMapPosition])

  useEffect(() => {
    if (!mapRef || (markerMap && Object.keys(markerMap).length === 0)) return;
    //Check if empty infowindow is open
    const infoWindows = document.querySelectorAll('div.gm-style-iw-a');
    if (!infoWindows.length) return;
    infoWindows.forEach(infoWindow => !infoWindow.querySelector('.citypage__map__info') && infoWindow.remove());
  }, [mapRef && mapRef.getZoom()])

  useEffect(() => {
    if (cityPageContext.mapZoom !== zoom)
      setZoom(cityPageContext.mapZoom);
  }, [contextMapZoom])

  useEffect(() => {
    if (!returnToOriginalPosition || !mapRef) return;
    setCityPageContext({ ...cityPageContext, returnToOriginalPosition: false });
    mapRef.panMapToCoordinates(+center.lat, +center.lng);
    if (mapRef.getZoom() !== zoom) mapRef.setZoom(zoom);
  }, [returnToOriginalPosition])

  useEffect(() => {
    if (contextLastKnownLocation !== lastKnownLocation && contextLastKnownLocation)
      setLastKnownLocation(contextLastKnownLocation);
  }, [contextLastKnownLocation])

  //useEffect(() => {
  //  if (!mapRef || !selectedPlace) return;
  //  if (infoOpen) handleClose();
  //}, [mapRef && mapRef.getZoom()])

  useEffect(() => {
    if (!isLoaded || !contextFilteredFacilities) return;
    setFacilities(contextFilteredFacilities);
  }, [isLoaded, contextFilteredFacilities])

  useEffect(() => {
    if (!mapRef || !facilities) return;
    fitBounds(!!(markerMap && Object.keys(markerMap).length === 0));
    const prevMarkerMap = markerMap;
    setMarkerMap({});
    const clusterer = addMarkersAndClusterer(mapRef, facilities, markerClickHandler, markerLoadHandler, prevMarkerMap, markerClusterer);
    if (clusterer) setMarkerClusterer(clusterer);
  }, [mapRef, facilities])

  useEffect(() => {
    if (!mapRef || showOnMapButtons) return;
    showOnMapButtons = document.querySelectorAll('[data-show-on-map]');
    showOnMapButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        setInfoOpen(false);
        panMapToCoordinates(+button.dataset.lat, +button.dataset.lng);
        setTimeout(() => {
          markerClickHandler(event, facilities.find(({ Uid }) => Uid === button.dataset.uid));
        }, 200);
      });
    })
  }, [mapRef])

  useEffect(() => {
    if (!mapRef) return;
    const buttons = document.querySelectorAll('[data-citypage-view]');;

    for (const button of buttons) {
      button.removeAttribute('disabled');
      button.addEventListener('click', (event) => {
        setViewToggle(button.dataset.citypageView);
        event.currentTarget.blur();
      });

    }

  }, [mapRef])

  useEffect(() => {
    const buttons = document.querySelectorAll('[data-citypage-view]');
    const views = document.querySelectorAll('[data-view-toggled]');

    for (const view of views) {
      view.dataset.viewToggled = view.className.includes(viewToggle);
    }

    for (const button of buttons) {
      if (viewToggle === button.dataset.citypageView) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    }

  }, [viewToggle])

  useEffect(() => {
    if (!mapRef) return;
    const icon = { url: MapMarkerIconCenter, alt: markerIconAlternativeText, scaledSize: new google.maps.Size(26, 26), anchor: new google.maps.Point(13, 26) };

    new google.maps.marker.AdvancedMarkerElement({
      map: mapRef,
      position: center,
      content: setImage(icon),
      zIndex: getZIndex(center.lat) - 30
    });
  },[mapRef])

  useEffect(() => {
    if (!mapRef || freeTextInputField) return;
    const locale = {
      ...cityPageContext.localization.headings,
      ...cityPageContext.localization.filter
    };

    freeTextInputField = document.querySelector('[data-city-page-filter] #filter-text');
    freeTextInputField.addEventListener('input', (event) => {
      // Only if reset-button in input field is clicked / pressed
      if (!event.data && !event.inputType) {
        setCityPageContext({
          ...cityPageContext,
          filteredFacilities: facilities,
          showLoadingScreen: false
        });
        for (const facility of facilities) {
          const listItem = document.querySelector(`li[data-uid=${facility.Uid}]`);
          listItem.dataset.visible = 'true';
          listItem.style.border = null;
        }
        const noresults = document.querySelector('[data-noresults]');
        if (noresults) noresults.remove();
      }
    });

    freeTextInputField.addEventListener('keyup', (event) => {
      if (event.key.toLowerCase() === 'enter')
      {
        event.preventDefault();

        const value = event.target.value.toLowerCase();
        let matches = [];

        for (const facility of facilities) {

          const listItem = document.querySelector(`li[data-uid=${facility.Uid}]`);

          let words = [
            facility.Uid,
            facility.Name,
            `${facility.Address.street} ${facility.Address.number}, ${facility.Address.postCode} ${facility.Address.city}`,
            facility.indoor ? locale.info.indoor : locale.info.outdoor
          ];

          if (facility.hasLtp) words.push(locale.info.LTP.replace('&shy;', ''));
          if (facility.hasStp) words.push(locale.info.STP.replace('&shy;', ''));
          if (facility.info.heated) words.push(locale.info.heated);

          for (const service of facility.services) {
            let key = service.toLowerCase();
            if (locale.services.hasOwnProperty(key)) {
              words.push(locale.services[key].replace('&shy;', ''));
            }
          }

          const string = words.toString().toLowerCase();
          const match = string.search(value) >= 0 ? true : false;

          if (match || value.length === 0) {
            listItem.dataset.visible = 'true';
            listItem.style.border = null;
            matches.push(facility);
          }

          if (!match) {
            listItem.dataset.visible = 'false';
            listItem.style.border = null;
          }

        }
        const noresults = document.querySelector('[data-noresults]') || document.querySelector('[data-nofacilities]');

        if (matches.length > 0) {
          if (noresults) noresults.remove();
          const last = matches[matches.length - 1];
          const element = document.querySelector(`li[data-uid=${last.Uid}]`);
          element.style.border = 'none';
        }

        setCityPageContext({
          ...cityPageContext,
          filteredFacilities: matches,
          showLoadingScreen: false
        });

        if (matches.length === 0 && !noresults) {
          const noresults = document.querySelector('[data-noresults]');
          const list = document.querySelector('.citypage__wrapper__list > ul');
          const newListItem = document.createElement('li');
          const newArticle = document.createElement('article');
          const newHeading = document.createElement('h2');

  //        if (!noresults) {
            newHeading.innerText = locale.nofacilities;
            newArticle.appendChild(newHeading);
            newListItem.appendChild(newArticle);
            newListItem.dataset.noresults = "true";
            list.appendChild(newListItem);
    //      };

          //panMapToCoordinates(cityPageContext.city.latitude, cityPageContext.city.longitude);
        }

      }
    });
    freeTextInputField.disabled = false;

  }, [mapRef, freeTextFilter])

  useEffect(() => {
    if (!cityPageContext.updateMapBounds || !mapRef || (viewToggle === 'list' && window.innerWidth < 989)) return;

    setDisableReload(true);
    fitBounds(false);
    setCityPageContext({ ...cityPageContext, updateMapBounds: false });
  }, [cityPageContext.updateMapBounds, viewToggle]);

  const checkFacilitiesInBounds = (facilities) => {
    if (!facilities) return
    const mapBounds = mapRef.getBounds();
    const markersInBounds = [];

    if (mapBounds) {
      for (var i = 0; i < facilities.length; i++) {

        const coordinates = {
          lng: facilities[i].Address.Marker.Coordinates.lng,
          lat: facilities[i].Address.Marker.Coordinates.lat,
        };

        if (mapBounds.contains(coordinates)) {
          markersInBounds.push(facilities[i]);
        }
      }
    }
    return { markersInBounds, mapBounds }
  }

  const panMapToCoordinates = (lat, lng) => {
    mapRef.panTo({ lat, lng });
    setTimeout(() => {
      mapRef.setZoom(zoom);
    }, 250);
  }

  const fitBounds = (initial) => {
    if (!mapRef || !facilities || facilities?.length === 0)
      return;

    const bounds = new window.google.maps.LatLngBounds();
    const filteredFacilities = contextFilteredFacilities ?? facilities;

    for (var i = 0; i < filteredFacilities.length; i++) {
      const coordinates = {
        lng: filteredFacilities[i].Address.Marker.Coordinates.lng,
        lat: filteredFacilities[i].Address.Marker.Coordinates.lat,
      };
      bounds.extend(coordinates);
    }

    mapRef.fitBounds(bounds);

    if (initial) {
    setTimeout(() => {
      if (mapRef.getZoom() > zoom) mapRef.setZoom(zoom);
    }, 1000);
    }
  };

  const loadHandler = map => {
    setMapRef(map);

    if (refContainer) {
      setCityPageContext({ ...cityPageContext, mapElementInDom: refContainer.current });
    }
  };

  const onIdleHandler = () => {
    if (!mapRef || !facilities) return;

    const mapBounds = mapRef.getBounds();
    const markersInBounds = [];
    let facilitiesInBounds = {};

    if (mapBounds) {
      facilitiesInBounds = checkFacilitiesInBounds(facilities)
      const currentCenterLocation = mapRef.center.toJSON();
      const lastKnownLocation = contextLastKnownLocation ?? lastKnownLocation;

      if (lastKnownLocation) {
        const distanceSinceLastLocation = calculateDistance(
          { latitude: currentCenterLocation.lat, longitude: currentCenterLocation.lng },
          { latitude: lastKnownLocation.lat, longitude: lastKnownLocation.lng }
        )

        const zoomOut = mapRef?.getZoom() < lastKnownZoom;
        setLastKnownZoom(mapRef?.getZoom());

        if (!disableReload && !cityPageContext.panOnLoad && !reloadButtonVisible && (distanceSinceLastLocation && (distanceSinceLastLocation > 1000 || markersInBounds.length === 0) || zoomOut)) {
          reloadMapClickHandler()
        }
      }
    }
    setCityPageContext({ ...cityPageContext, visibleFacilities: facilitiesInBounds.markersInBounds, mapBounds });
  };

  const markerLoadHandler = (marker, place, index) => {
    let prevMarkerMap = markerMap;
    prevMarkerMap[place.Uid] = marker;

    setMarkerMap(prevMarkerMap);
    if (cityPageContext.showLoadingScreen && cityPageContext.filteredFacilities && index === cityPageContext.filteredFacilities.length - 1) {
      const facilitiesInBounds = checkFacilitiesInBounds(cityPageContext.filteredFacilities);
      setCityPageContext({
        ...cityPageContext,
        visibleFacilities: facilitiesInBounds?.markersInBounds,
        showLoadingScreen: false
      });
    }
  };

  const markerClickHandler = async (event, place) => {
    handleClose();
    setKeyboardEvent(false);
    if (event.domEvent instanceof KeyboardEvent) {
      setKeyboardEvent(true);
    }
    const icon = place.indoor ? { url: MapMarkerRoofIconSelected, alt: markerIconAlternativeText, scaledSize: new google.maps.Size(40, 49), anchor: new google.maps.Point(20, 49) } :
        { url: MapMarkerIconSelected, scaledSize: new google.maps.Size(40, 49), anchor: new google.maps.Point(20, 49) }
    markerMap[place.Uid].content = setImage(icon);
    setSelectedPlace(place);
    setActiveMarker({ pos: [place.Address.Marker.Coordinates.lng, place.Address.Marker.Coordinates.lat] });
    setInfoOpen(true);
  };

  const reloadMapClickHandler = async () => {
    return
  };

  const setImage = (icon, img) => {
    const image = img ?? document.createElement('img');
    image.setAttribute('src', icon.url);
    image.setAttribute('height', icon.scaledSize.height);
    image.setAttribute('width', icon.scaledSize.width);
    image.setAttribute('alt', icon.alt);
    return image;
  }

  const handleClose = () => {
    setInfoOpen(false);
    setActiveMarker(null);
    if (markerMap && Object.keys(markerMap).length > 0) {
      for (const [key, value] of Object.entries(markerMap)) {
        const facility = facilities.find(facility => facility.Uid === key);
        const icon = facility?.indoor ? { url: MapMarkerRoofIcon, alt: markerIconAlternativeText, scaledSize: new google.maps.Size(30, 33), anchor: new google.maps.Point(15, 33) } :
          { url: MapMarkerIcon, scaledSize: new google.maps.Size(30, 33), anchor: new google.maps.Point(15, 33) }
        value.content = setImage(icon)
      }
    }
  }

  const renderMap = (lang) => {
    const markerIconCenter = { url: MapMarkerIconCenter, scaledSize: new google.maps.Size(26, 26), anchor: new google.maps.Point(13, 26) }

    return (
      <div className={['citypage__map', cityPageContext.mapViewOpen ? 'citypage__map--fullscreen' : null].join(' ')} ref={refContainer} style={maxHeight}>
        {reloadButtonVisible && (
          <button className="citypage__map__reload" disabled={reloadButtonPending} onClick={reloadMapClickHandler}>
            {lang.reloadmap}
          </button>
        )}

        {cityPageContext.showLoadingScreen && (
          <LoadingScreenWidget />
        )}

        <GoogleMap
          onLoad={loadHandler}
          onIdle={onIdleHandler}
          onClick={handleClose}
          onDrag={() => setDisableReload(false) }
          center={center}
          zoom={zoom}
          options={mapOptions}
          mapContainerStyle={{
            height: "100%",
            width: "100%"
          }}
        >

          {infoOpen && selectedPlace && (
            <InfoWindowWidget
              keyboardEvent={keyboardEvent}
              cityPageContext={cityPageContext}
              anchor={markerMap[selectedPlace.Uid]}
              selectedPlace={selectedPlace}
              map={mapRef}
              onClose={handleClose}
            />
          )}
        </GoogleMap>
        {mapRef && <MapControlsWidget map={mapRef} />}
      </div>
    );
  };

  return isLoaded ? renderMap(lang) : <div className="citypage__map"><div className="citypage__map__overlay"><div className="aimo-spinner"><span></span><span></span><span></span><span></span></div></div></div>;
  
}

export default MapWidget;
